<template>
    <div v-if="pageData.data" class="palestine-timeline">
        <div class="zone-title zone-title--bg banded landing__zone-title--short">
            <div class="gradient">
                <div class="container-title container-headline container-headline--no-image">
                    <h1>A Timeline of U.S. Policy Toward Palestine</h1>
                </div>
            </div>
        </div>
        <section class="top-padding-standard bottom-padding-standard background-grey tier">
            <div class="inner cols">
                <div class="col col-50">
                    <div class="gutters">
                        <h4>About this Timeline</h4>
                        <p>This timeline is part of the  Breaking the Israel-Palestine Status Quo: A Rights-Based Approach Project.</p>
                    </div>
                </div>
                <div class="col col-50">
                    <div class="gutters">
                        <a class="anchor button-secondary theme-light size-regular icon-position-right button carnegie-button button" href="https://carnegieendowment.org/projects/breaking-the-israel-palestine-status-quo-a-rights-based-approach?lang=en">
                            <span class="label direction-ltr typography label">More about the project</span>
                        </a>
                    </div>
                </div>
            </div>
        </section>
        <div class="section background white intro">
            <div class="section foreground">
                    <p>This timeline chronicles some of the major events and legislation that have influenced the United States’ bilateral relationship with the Palestine Liberation Organization (PLO), the internationally recognized representative of the Palestinian people. It also highlights milestones in the history of U.S. engagement on Israel-Palestine peacemaking. </p>
                    <p>The fragile and tentative nature of the U.S.-PLO relationship began with secret talks in the 1980s and culminated in the very public closure of the PLO’s mission to the United States in 2018. Along with the U.S. Executive Branch, Congress has played and continues to play a critical role in defining the conditions and parameters for diplomatic ties as well as for a peace acceptable to the United States. Whether full diplomatic relations—including the return of a PLO representative to Washington—are possible depends as much on Congress as it does on the president.</p>
                    <div class="legend">
                        <div class="cols"> 
                            <div class="col col-20">
                                <div class="gutter"> 
                                    <span class="gray"></span>
                                    <div class="legend-text">Influencing Event</div>
                                </div>
                            </div>
                            <div class="col col-20"> 
                                <div class="gutter">
                                    <span class="green"></span>
                                    <div class="legend-text">Current</div>
                                </div>
                            </div>
                            <div class="col col-20"> 
                                <div class="gutter">
                                    <span class="yellow"></span>
                                    <div class="legend-text">Current With Amendments</div>
                                </div>
                            </div>
                            <div class="col col-20"> 
                                <div class="gutter">
                                    <span class="red"></span>
                                    <div class="legend-text">Expired</div>
                                </div>
                            </div>
                            <div class="col col-20"> 
                                <div class="gutter">
                                    <span class="dark-blue"></span>
                                    <div class="legend-text">Executive Action</div>
                                </div>
                            </div>
                        </div>
                    </div>
            </div>
        </div>
        <div class="section timeline">
            <div class="foreground">
                <div class="frst-container" data-animation-name="slideInUp" id="timeline-start">
                    <!--frst-left-align/frst-right-align/frst-alternate/frst-date-opposite/frst-responsive-right-->
                    <div class="frst-timeline frst-timeline-style-8">
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>1983</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="ECBGlitch2020">
                            <div class="frst-timeline-img"><span class="gray"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner gray">
                                    <h2>1983–1984</h2>
                                    <p><a href="https://www.nytimes.com/1984/02/19/world/reagan-administration-held-9-month-talks-with-plo.html">Secret talks are held</a> between the Palestine Liberation Organization (PLO) and officials from president Ronald Reagan’s administration
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>1985</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="green"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner green">
                                    <h2>August 8, 1985</h2>
                                    <p><a href="https://www.govinfo.gov/content/pkg/STATUTE-99/pdf/STATUTE-99-Pg190.pdf">U.S. Congress codifies</a> policy prohibiting negotiations with the PLO
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>1987</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="gray"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner gray">
                                    <h2>December 9, 1987</h2>
                                    <p><a href="https://www.middleeastmonitor.com/20171209-remembering-the-first-intifada-2/">An Israeli military truck strikes</a> and kills Palestinian day laborers, sparking the first Palestinian uprising
                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="green"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner green">
                                    <h2>December 27, 1987</h2>
                                    <p><a href="https://uscode.house.gov/view.xhtml?path=/prelim@title22/chapter61&edition=prelim">Anti-Terrorism Act</a> labels the PLO a terrorist organization
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>1990</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="green"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner green">
                                    <h2>February 16, 1990</h2>
                                    <p><a href="https://www.govinfo.gov/content/pkg/STATUTE-104/pdf/STATUTE-104-Pg15.pdf">Foreign Relations Authorization Act</a> prohibits U.S. funds for the United Nations or any of its agencies that accord the PLO the same standing as
                                        a member state
                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="yellow"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner yellow">
                                    <h2>November 29, 1990</h2>
                                    <p><a href="https://www.justice.gov/sites/default/files/eoir/legacy/2009/03/04/IMMACT1990.pdf">Immigration Act of 1990</a> amends immigration laws to assume that officials and spokespersons of the PLO are engaged in terrorism and are
                                        therefore excludable
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>1993</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="gray"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner gray">
                                    <h2>September 13, 1993</h2>
                                    <p><a href="https://www.cnn.com/2013/09/03/world/meast/oslo-accords-fast-facts/index.html">Oslo Accords</a> are signed by Israel and the PLO, launching negotiations
                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="red"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner red">
                                    <h2>October 28, 1993</h2>
                                    <p><a href="https://www.govinfo.gov/content/pkg/STATUTE-107/pdf/STATUTE-107-Pg1309.pdf">Middle East Peace Facilitation Act</a> (MEPFA) grants the president the flexibility to suspend provisions preventing the PLO from operating in the
                                        United States (expired in 1997)
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>1994</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="gray"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner gray">
                                    <h2>1994</h2>
                                    <p>The <a href="https://fas.org/sgp/crs/mideast/RL34074.pdf">PLO opens a representative office</a> in Washington
                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="green"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner green">
                                    <h2>April 30, 1994</h2>
                                    <p><a href="https://www.govinfo.gov/content/pkg/STATUTE-108/pdf/STATUTE-108-Pg382.pdf">Foreign Relations Authorization Act</a> prohibits U.S. funds for the United Nations or any affiliated organizations that grant membership to an organization
                                        or group that does not have the internationally recognized attributes of statehood
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>1995</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="gray"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner gray">
                                    <h2>1995</h2>
                                    <p>Oslo peace process continues
                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="gray"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner gray">
                                    <h2>1988–1995</h2>
                                    <p>A <a href="https://scholarship.law.georgetown.edu/cgi/viewcontent.cgi?article=1073&context=facpub">series of federal cases</a> call into question the use of secret evidence to deport or deny immigration benefits to persons—mainly Arabs
                                        and Palestinians—<a href="https://heinonline.org/HOL/Page?public=true&handle=hein.journals/geoimlj14&div=10&start_page=51&collection=journals&set_as_cursor=0&men_tab=srchresults">because of their political associations and advocacy</a>

                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="gray"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner gray">
                                    <h2>1993–1995</h2>
                                    <p><a href="https://www.state.gov/1993-world-trade-center-bombing/">World Trade Center</a> and the <a href="https://www.nytimes.com/1995/04/25/us/terror-oklahoma-overview-oklahoma-bombing-plotted-for-months-officials-say.html">Oklahoma City federal building</a>                        are bombed, fueling concerns about terrorism
                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="green"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner green">
                                    <h2>November 8, 1995</h2>
                                    <p><a href="https://www.congress.gov/104/plaws/publ45/PLAW-104publ45.pdf">Jerusalem Embassy Act</a> calls for relocating the U.S. embassy from Tel Aviv to Jerusalem by 1999
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>1996</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="yellow"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner yellow">
                                    <h2>April 24, 1996</h2>
                                    <p><a href="https://www.congress.gov/104/plaws/publ132/PLAW-104publ132.pdf">Anti-Terrorism and Effective Death Penalty Act</a> amends immigration laws to make officials and spokespersons of designated foreign terrorist organizations excludable
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>1997</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="dark-blue"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner dark-blue">
                                    <h2>1997</h2>
                                    <p><a href="https://www.state.gov/foreign-terrorist-organizations/">U.S. State Department designates</a> certain PLO factions and other Palestinian groups as foreign terrorists

                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="red"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner red">
                                    <h2>November 26, 1997</h2>
                                    <p><a href="https://www.congress.gov/105/plaws/publ118/PLAW-105publ118.pdf">Foreign Operations, Export Financing, and Related Programs Appropriations Act</a> grants the president the authority to suspend restrictions on PLO operations
                                        in the United States, replacing the MEPFA
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>2000</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="gray"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner gray">
                                    <h2>September 28, 2000</h2>
                                    <p><a href="https://www.aljazeera.com/news/2003/12/4/the-second-intifada">Israeli prime minister candidate Ariel Sharon enters the Al Aqsa Mosque</a> in Jerusalem with Israeli soldiers and police, trigging the second Palestinian uprising
                                        in the occupied territories
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>2002</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="green"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner green">
                                    <h2>September 30, 2002</h2>
                                    <p><a href="https://www.govinfo.gov/content/pkg/PLAW-107publ228/html/PLAW-107publ228.htm">Foreign Relations Authorization Act</a> allows the Secretary of State to make PLO and Palestinian Authority (PA) officials ineligible for a visa
                                        unless a waiver is granted
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>2006</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="gray"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner gray">
                                    <h2>January 2006</h2>
                                    <p><a href="https://www.washingtonpost.com/archive/politics/2006/01/27/hamas-sweeps-palestinian-elections-complicating-peace-efforts-in-mideast/8a4a4412-5f9b-4583-8607-51c7dd3781f4/">Hamas wins</a> Palestinian Legislative Council elections
                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="green"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner green">
                                    <h2>December 21, 2006</h2>
                                    <p><a href="https://www.govinfo.gov/content/pkg/PLAW-109publ446/html/PLAW-109publ446.htm">Palestinian Anti-Terrorism Act</a> prohibits U.S. assistance to any Palestinian unity government with Hamas or Hamas-controlled institutions unless
                                        Hamas recognizes Israel, renounces political violence, and affirms agreements signed by the PLO and Israel
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>2011</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="gray"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner gray">
                                    <h2>October 31, 2011</h2>
                                    <p><a href="https://news.un.org/en/story/2011/10/393562-unesco-votes-admit-palestine-full-member">Palestine joins</a> the UN Educational, Scientific and Cultural Organization (UNESCO) as a member state

                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="gray"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner gray">
                                    <h2>October 31, 2011</h2>
                                    <p><a href="https://www.cbsnews.com/news/us-cuts-unesco-funding-after-palestinian-vote/">U.S. cuts funding to UNESCO</a> because of its vote to admit the State of Palestine as a member

                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="yellow"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner yellow">
                                    <h2>December 23, 2011</h2>
                                    <p><a href="https://www.govinfo.gov/content/pkg/PLAW-112publ74/html/PLAW-112publ74.htm">Consolidated Appropriations Act</a> mandates a cut-off in all U.S. economic aid to the PA if the PLO takes action at the UN to obtain full membership
                                        and/or joins specialized agencies, unless a waiver is granted

                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>2014</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="gray"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner gray">
                                    <h2>2009–2014</h2>
                                    <p><a href="https://www.nytimes.com/2014/09/11/world/middleeast/gaza-strip-israel-criminal-investigation.html">Following military assaults</a> on Gaza, concerns grow that Palestinian leaders may initiate war crimes investigations against
                                        Israeli officials

                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="green"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner green">
                                    <h2>January 7, 2014</h2>
                                    <p><a href="https://www.govinfo.gov/content/pkg/PLAW-113publ235/html/PLAW-113publ235.htm">Consolidated Appropriations Act of 2014</a> places additional limitations on Palestinians initiating or supporting an International Criminal Court
                                        (ICC)-authorized investigation of Israeli nationals for crimes against Palestinians, including a cut-off of all economic aid without possibility of a waiver

                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="gray"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner gray">
                                    <h2>April 2014</h2>
                                    <p><a href="https://www.washingtonpost.com/world/middle_east/israel-suspends-peace-talks-with-palestinians/2014/04/24/659aa218-cbc6-11e3-a75e-463587891b57_story.html">Israeli-Palestinian peace talks</a> end after Israel refuses to release
                                        last tranche of Palestinian prisoners pursuant to conditions for U.S. sponsored talks

                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="yellow"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner yellow">
                                    <h2>December 16, 2014</h2>
                                    <p><a href="https://www.govinfo.gov/content/pkg/PLAW-113publ235/html/PLAW-113publ235.htm">Consolidated and Further Continuing Appropriations Act</a> requires a dollar-for-dollar reduction in economic aid to the PA in the amount spent
                                        on welfare payments to prisoners and the families of those killed during acts of political violence

                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>2016</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="dark-blue"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner dark-blue">
                                    <h2>December 28, 2016</h2>
                                    <p><a href="https://2009-2017.state.gov/secretary/remarks/2016/12/266119.htm">Outgoing U.S. secretary of state John Kerry sets</a> out an additional parameter for Israeli-Palestinian peace: recognition of Israel as a state for Jewish
                                        people

                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>2017</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="dark-blue"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner dark-blue">
                                    <h2>December 6, 2017</h2>
                                    <p><a href="https://www.nytimes.com/2017/12/06/world/middleeast/trump-jerusalem-israel-capital.html">U.S. president Donald Trump recognizes</a> Jerusalem as the capital of Israel and announces plans to move the U.S. embassy from Tel Aviv
                                        to Jerusalem

                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>2018</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="gray"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner gray">
                                    <h2>February 22, 2018</h2>
                                    <p><a href="https://www.supremecourt.gov/DocketPDF/16/16-1071/36298/20180222171849460_16-1071 Sokolow.pdf">Sokolow v.  PLO/PA</a>, involving liability for terrorism in Israel, comes before the U.S. Supreme Court

                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="green"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner green">
                                    <h2>March 23, 2018</h2>
                                    <p><a href="https://www.govinfo.gov/content/pkg/BILLS-115hr1625enr/pdf/BILLS-115hr1625enr.pdf">Consolidated Appropriations Act</a> includes the Taylor Force Act, which mandates the cut-off of all economic aid that “directly benefits”
                                        the PA until it and the PLO end the welfare payment system for families of prisoners and those killed during acts of political violence, in favor of a strictly needs-based system

                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="gray"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner gray">
                                    <h2>April 2, 2018</h2>
                                    <p><a href="https://www.nbcnews.com/politics/supreme-court/supreme-court-won-t-hear-case-involving-american-victims-palestinian-n862036">U.S. Supreme Court</a> denies the petition for review of <em>Sokolow v. PA/PLO</em>, leaving intact
                                        the appellate court’s decision that there is no basis for it to exercise personal jurisdiction over the entities

                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="gray"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner gray">
                                    <h2>July 19, 2018</h2>
                                    <p><a href="https://knesset.gov.il/laws/special/eng/BasicLawNationState.pdf">Israeli Knesset passes</a> Basic Law: Israel as the Nation State of the Jewish People, enshrining with quasi-constitutional authority that only Jews have a right
                                        to self-determination in areas Israel asserts as comprising its state

                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="dark-blue"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner dark-blue">
                                    <h2>September 10, 2018</h2>
                                    <p><a href="https://www.axios.com/state-department-palestine-liberation-organization-plo-dc-office-449cc9ea-e6ce-4e41-8a35-7e700115f1ce.html">U.S. orders closure</a> of the PLO office in Washington due to the PLO’s refusal to engage with
                                        the White House on its Middle East peace plan

                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="yellow"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner yellow">
                                    <h2>October 3, 2018</h2>
                                    <p><a href="https://www.congress.gov/115/plaws/publ253/PLAW-115publ253.pdf">Anti-Terrorism Clarification Act</a> (ATCA) requires any entity receiving U.S. foreign assistance to accept civil liability for acts of terrorism committed abroad
                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="gray"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner gray">
                                    <h2>December 26, 2018</h2>
                                    <p><a href="https://www.timesofisrael.com/with-new-anti-terrorism-law-going-into-effect-pa-will-stop-accepting-us-aid/">Palestinian prime minister Rami Hamdallah sends notice</a> of the PA’s intention to no longer accept U.S. assistance
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>2019</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="dark-blue"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner dark-blue">
                                    <h2>March 4, 2019</h2>
                                    <p><a href="https://www.bbc.com/news/world-middle-east-47442145">U.S. merges consulate</a> in Jerusalem serving as its de facto mission to Palestinians with the U.S. embassy to Israel

                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="green"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner green">
                                    <h2>December 11, 2019</h2>
                                    <p><a href="https://www.federalregister.gov/documents/2019/12/16/2019-27217/combating-anti-semitism">Executive Order on Combating Anti-Semitism</a> calls for treating anti-Semitism as a prohibited category of discrimination under Title
                                        VI of the Civil Rights Act and requires federal agencies to consider certain criticism of Israel as evidence of discriminatory intent

                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="green"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner green">
                                    <h2>December 20, 2019</h2>
                                    <p><a href="https://www.govinfo.gov/content/pkg/PLAW-116publ94/pdf/PLAW-116publ94.pdf">Further Consolidated Appropriations Act</a> includes the Promoting Security and Justice for Victims of Terrorism Act (PSJVTA), which amends the ATCA
                                        by replacing acceptance of U.S. aid as a trigger for consent to personal jurisdiction with PLO/PA official presence/activity in the United States and/or continuing social welfare payments to prisoners and their families
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>2020</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="dark-blue"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner dark-blue">
                                    <h2>January 28, 2020</h2>
                                    <p><a href="https://apnews.com/article/f7d36b9023309ce4b1e423b02abf52c6">Trump administration unveils</a> Peace to Prosperity plan, which allowed for at least 30 percent of the West Bank to be annexed to Israel with limited autonomy for
                                        Palestinians in West Bank enclaves

                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="gray"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner gray">
                                    <h2>April 27, 2020</h2>
                                    <p><a href="https://www.supremecourt.gov/docket/docketfiles/html/public/19-764.html">U.S. Supreme Court orders</a> the Second Circuit Court of Appeals to revisit its decision dismissing <em>Sokolow v. PLO/PA</em> in light of passage of
                                        the PSJVTA
                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="dark-blue"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner dark-blue">
                                    <h2>December 24, 2020</h2>
                                    <p><a href="https://www.upi.com/Top_News/US/2020/12/24/US-rule-change-labels-goods-from-West-Bank-area-as-Made-in-Israel/4301608823858/">Trump administration orders</a> all products from Area C (60 percent of the occupied West Bank) to
                                        be marked as “Made in Israel” or “Product of Israel” for customs purposes

                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
                            <div class="frst-labels frst-start-label"><span>2021</span></div>
                            <!-- .frst-start-label-->
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="gray"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner gray">
                                    <h2>March 3, 2021</h2>
                                    <p><a href="https://www.reuters.com/article/us-warcrimes-icc-palestinians/icc-prosecutor-to-probe-war-crimes-in-palestinian-territories-angering-israel-idUSKBN2AV1R0?il=0">ICC opens investigation</a> into war crimes committed by both
                                        Israelis and Palestinians in the occupied Palestinian territories, drawing criticism from the United States, which opposes the court’s acceptance of jurisdiction over Israeli officials

                                    </p>
                                </div>
                            </div>
                        </div>
                        <!--frst-timeline-block-->
                        <div class="frst-timeline-block frst-even-item" data-animation="slideInUp" id="Vizom2020">
                            <div class="frst-timeline-img"><span class="dark-blue"></span></div>
                            <!-- frst-timeline-img -->
                            <div class="frst-timeline-content swing" style="position: relative;">
                                <div class="frst-timeline-content-inner dark-blue">
                                    <h2>April 7, 2021</h2>
                                    <p><a href="hhttps://www.state.gov/the-united-states-restores-assistance-for-the-palestinians/">Administration of U.S. President Joe Biden announces</a> the resumption of some bilateral assistance to the West Bank and Gaza and $150 million
                                        in humanitarian assistance to the United Nations Relief and Works Agency for Palestine Refugees in the Near East (UNRWA)</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
            </div>
        </div>
    </div>
</template>

<script setup>
import FeatureFragment from '@fragments/pages/FeatureFragment.ts';

const pageData = await usePageData(FeatureFragment, 'features');
</script>

<style lang="scss" scoped>
$gutter: 30px;
$site-width: 1000px;

$gold: #a28556;
$black: #000;
$white: #fff;
$teal: #00a79d;
$dark-gray-new: #6e6e71;
$light-gray-new: #e6e7e8;
$dark-blue: #00365e;
$yellow: #fbb040;
$red: #ef4136;
$carnegie-blue: #003a63;
$tertiary: $dark-gray-new; //$gray-dark;

$roman-normal-book: 'Apercu-Medium', Arial, sans-serif;
$roman-italic-book: 'Apercu-Medium-Italic', Arial, sans-serif;
$roman-normal-bold: 'Apercu-Bold', 'Arial Black', sans-serif;
$roman-italic-bold: 'Apercu-Bold-Italic', 'Arial Black', sans-serif;

%bold-sans-text {
    font-family: $roman-normal-bold;
    font-weight: 700;
}

%italic-sans-text {
    font-family: $roman-normal-book;
    font-style: italic;
}

.palestine-timeline {

    //layout basics
    .foreground {
        position: relative;
        margin: 0 auto;
        box-sizing: content-box;
        max-width: $site-width;
        padding-left: calc($gutter / 2);
        padding-right: calc($gutter / 2);
    }

    .section {
        display: block;
        clear: both;
        margin-top: $gutter;
        margin-bottom: $gutter;
    }

    .cols {
        clear: both;
        //use a .no-gutters class to undo these default styles
        margin-left: calc($gutter / 2) * -1;
        margin-right: calc($gutter / 2) * -1;

        &:before,
        &:after {
            content: ' ';
            display: table;
            height: 0;
            clear: both;
            font-size: 1px;
            line-height: 0px;
            visibility: hidden;
        }
    }

    .cols--equal-heights {
        flex-wrap: wrap;

        &,
        >.col {
            display: flex;

            // @include bp(mobile) {
            //     display: block;
            // }
        }

        >.col {
            flex-direction: column;
        }

        .banded {
            flex: 1 0 auto;
        }
    }

    .cols {
        clear: both;
    }

    .col {
        float: left;

        .gutters {
            padding-left: calc($gutter / 2);
            padding-right: calc($gutter / 2);
        }

        // @include bp(mobile) {
        //     float: none;
        //     width: 100% !important;

        //     > .gutters {
        //         margin-left: 0;
        //         margin-right: 0;
        //     }
        // }
    }

    .col-80 {
        width: 80%;
    }

    .col-70 {
        width: 70%;
    }

    //two across
    .col-50 {
        width: 50%;
    }

    .col-33 {
        width: 33%;
    }

    .col-30 {
        width: 30%;
    }

    //four across
    .col-25 {
        width: 25%;
    }

    //five across
    .col-20 {
        width: 20%;
    }

    .padded {
        padding: $gutter;
    }

    .gutter-right {
        margin-right: 30px;
    }

    .gutter-left {
        margin-left: 30px;
    }

    :deep(.accordion-content) {
        display: none;

        &.active {
            display: block;
            margin-top: 3rem;
        }
    }

    .section {
        font-family: $roman-normal-book;
        margin-bottom: 0;
        margin-top: 0;
        z-index: 3;
    }

    .zone-title {
        background-attachment: fixed;
        background-color: $white;
        background-image: url(https://carnegie-production-assets.s3.amazonaws.com/static/media/images/hdr-breakingIsraelPalestineStatusQuo.jpg);
        background-position: right top;
        background-size: 120% auto;
        background-repeat: no-repeat;
        height: 300px;
        text-align: left;

        .container-title {
            padding-top: 140px;
            margin: 0 0 0 4%;
            width: 55%;
        }

        h1 {
            color: $white;
            display: inline-block;
            font-family: $roman-normal-bold;
            font-size: 4rem;
            margin: $gutter 0;
            padding: calc($gutter / 5) 20px 12px 20px;
        }
    }

    .background {
        padding: calc($gutter * 2) 0;
        position: relative;
    }

    .intro {
        border-top: none;
        display: block;
        position: relative;
        z-index: 3;

        .foreground {
            margin-top: $gutter;
            margin-bottom: $gutter;
        }

        p {
            margin-bottom: 1.3rem;
        }
        
    }

    .button-secondary {
        border: 1px solid rgb(var(--color-rgb-teal-default));
        border-radius: 5px;
        color: rgba(var(--color-rgb-blue-default),.7);
        gap: .8rem;
        text-decoration: none;
        padding: 1.6rem 2.6rem 1.8rem;
        transition-duration: 125ms;
        align-items: center;
        display: inline-flex;
        flex-wrap: wrap;
        text-align: center;
        transition-property: background-color,color;
        transition-timing-function: ease-in;
    }

    .tier {
        background: rgb(var(--color-rgb-offblanc-default));
        display: block;
        padding-bottom: 6.4rem;
        padding-top: 6.4rem;

        .inner {
            margin: 0 auto;
            max-width: 144rem;
            padding-left: var(--page-margin);
            padding-right: var(--page-margin);
            width: 100%;

            h4 {
                font-family: GT Sectra,Georgia,serif;
                font-size: 2.4rem;
                font-weight: 350;
                line-height: 1.1666666667;
            }

            p {
                font-family: GT Sectra,Georgia,serif;
                font-size: 1.6rem;
                font-weight: 350;
                line-height: 1.5;
            }
        }
    }



.frst-container {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;

    /* this class is used to give a max-width to the element it is applied to, and center it horizontally when it reaches that max-width */
    max-width: 1200px;
    margin: 0 auto;
    padding: 100px 15px;
}

.frst-container::after {
    /* clearfix */
    content: '';
    display: table;
    clear: both;
}

/* --------------------------------

Main components

-------------------------------- */
.frst-timeline {
    position: relative;
}

.frst-right-align {
    text-align: right;
}

/*.frst-date-opposite{
padding: 0;
}*/

.frst-timeline-block {
    position: relative;
    min-height: 16px;
    padding-left: 35px;
    padding-right: 35px;
}

.frst-timeline-block::before {
    content: '';
    position: absolute;
    height: 100%;
    left: 0;
    top: 0;
}

.frst-timeline-block:last-child::before {
    display: none;
}

.frst-left-align .frst-timeline-block::before {
}

.frst-right-align .frst-timeline-block::before {
    right: 0;
    left: auto !important;
    margin-left: 0 !important;
}

.frst-timeline-block::after {
    content: '';
    display: table;
    clear: both;
}

.frst-timeline-img {
    position: absolute;
    min-width: 1px;
    min-height: 1px;
    left: 0;
    top: 0;
}

.frst-timeline-img i {
    display: block;
}

.frst-right-align .frst-timeline-img {
    margin-left: 0 !important;
    left: auto !important;
    right: 0;
}

.frst-last-empty-item .frst-timeline-img span {
    vertical-align: top;
}

.frst-timeline-content-inner {
    position: relative;
}

.frst-date {
    display: block;
}

.cssanimations .frst-timeline-content.is-hidden {
    visibility: hidden;
}

.cssanimations .frst-timeline-content.animated {
    visibility: visible;
}

.frst-labels span {
    display: inline-block;
}

.frst-labels {
    padding: 20px 0;
}

.frst-labels.frst-start-label {
    padding-top: 0 !important;
}

.frst-labels.frst-end-label {
    margin-bottom: 0 !important;
}

.frst-container .turqoise {
    background: #00a79d !important;
}

.frst-container .light-blue {
    background: #27aae2 !important;
}

.frst-container .blue {
    background: #1d75bd !important;
}

.frst-container .violette {
    background: #2b3991 !important;
}

.frst-container .dark-violette {
    background: #272264 !important;
}

/*.aqua-blue{
  background: #36ACB6 !important;
}
.red{
  background: #C84028 !important;
}
.orange{
  background: #EB8D23 !important;
}
.light-green{
  background: #C2C84A !important;
}*/

@media only screen and (min-width: 679px) {
    .frst-date-opposite {
        margin-left: 190px;
    }

    .frst-left-align.frst-date-opposite {
        margin-left: 190px;
    }

    .frst-right-align.frst-date-opposite {
        margin-right: 190px;
        margin-left: 0;
        text-align: right;
    }

    .frst-date-opposite.frst-alternate {
        margin: 0;
    }

    .frst-timeline.frst-alternate.frst-left-align .frst-timeline-content,
    .frst-timeline.frst-date-opposite.frst-left-align .frst-timeline-content,
    .frst-timeline.frst-alternate.frst-right-align .frst-timeline-content,
    .frst-timeline.frst-date-opposite.frst-right-align .frst-timeline-content {
        width: 100%;
    }

    .frst-alternate .frst-timeline-block::before {
        left: 50%;
    }

    .frst-alternate .frst-timeline-img {
        left: 50% !important;
    }

    .frst-alternate .frst-timeline-block.frst-even-item .frst-timeline-content {
        float: right;
    }

    .frst-alternate .frst-timeline-block.frst-odd-item .frst-timeline-content {
        float: left;
    }

    .frst-date-opposite .frst-date {
        position: absolute;
        top: 0;
        text-align: right;
    }

    .frst-alternate .frst-timeline-block.frst-odd-item .frst-timeline-content,
    .frst-alternate.frst-date-opposite .frst-timeline-block.frst-even-item .frst-date,
    .frst-date-opposite.frst-left-align .frst-date,
    .frst-right-align {
        text-align: right;
    }

    .frst-alternate.frst-date-opposite .frst-timeline-block.frst-odd-item .frst-timeline-content .frst-date,
    .frst-date-opposite.frst-right-align .frst-date {
        text-align: left;
    }

    .frst-alternate .frst-timeline-label-block {
        text-align: center;
    }

    .frst-alternate .frst-timeline-label-block .frst-labels span {
        left: 0;
        right: 0;
    }
}

@media only screen and (max-width: 678px) {
    .frst-timeline {
        margin-left: 20px;
    }

    .frst-timeline.frst-right-align,
    .frst-timeline.frst-responsive-right {
        margin-right: 20px;
        margin-left: 0;
    }

    .frst-responsive-right .frst-timeline-block::before {
        right: 0;
        left: auto !important;
        margin-left: 0 !important;
    }

    .frst-responsive-right {
        text-align: right;
    }

    .frst-responsive-right .frst-timeline-img {
        margin-left: 0 !important;
        left: auto !important;
        right: 0;
    }

    .frst-date {
        margin-bottom: 10px;
    }
}

/*-------- Type- 01 ------*/

.frst-timeline-style-8 {
    color: #000;
}

.frst-timeline-style-8 .frst-timeline-block::before {
    width: 3px;
    background: rgba(0, 0, 0, 0);
    margin-left: -2px;
    background: rgba(12, 12, 12, 0.8);
    top: 12px;
}

.frst-timeline-style-8.frst-right-align .frst-timeline-block::before {
    margin-right: -2px;
}

.frst-timeline-block {
    padding-bottom: 30px;
    padding-left: 45px;
    padding-right: 45px;
}

.frst-timeline-style-8 .frst-timeline-img {
    padding: 0;
    margin-left: -21px;
    top: 6px;
    /*--- Box shadow value --*/
    width: 42px;
    height: 42px;
    color: #fff;
    text-align: center;
    font-size: 24px;
}

.frst-timeline-style-8 .frst-timeline-img span {
    display: inline-block;
    margin: auto;
    padding: 6px;
    height: 50%;
    width: 50%;
    background: #202020;
    -webkit-border-radius: 50%;
    border-radius: 50%;
    vertical-align: top;
    border: 3px solid #202020;
    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.5);
    -webkit-box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.5);
    vertical-align: middle;
}

.frst-timeline-style-8 .frst-timeline-img i {
    display: block;
}

.frst-timeline-style-8.frst-right-align .frst-timeline-img {
    margin-right: -21px;
}

.frst-timeline-style-8 .frst-timeline-content {
    color: #777;
    background: white;
}

.frst-timeline-style-8 .frst-date {
    color: #9c9c9c;
}

.frst-timeline-style-8 h2 {
    margin: 0 0 5px;
    font-weight: 800;
    color: #444;
    font-size: 18px;
}

.frst-date-opposite.frst-timeline-style-8 h2 {
    font-family: 'Frutiger Neue LT W06 Book', Arial, sans-serif;
    margin-top: 1em;
}

.frst-timeline-style-8 p {
    margin: 0;
    font-family: 'Frutiger Neue LT W06 Thin', Arial, sans-serif;
    font-size: 0.75em;
    line-height: normal;
}

.frst-timeline-style-8 .frst-timeline-label-block {
    padding-top: 0;
}

.frst-timeline-style-8 .frst-labels span {
    font-family: 'Frutiger Neue LT W06 Book', Arial, sans-serif;
    position: relative;
    background: #202020;
    color: #fff;
    font-weight: 800;
    padding: 8px 30px;
    -webkit-border-radius: 4px;
    border-radius: 4px;
    text-transform: uppercase;
    text-align: center;
    font-size: 14px;
}

.frst-timeline-style-8.frst-right-align .frst-labels span {
    left: auto;
    right: -47px;
}

.frst-timeline-style-8.frst-left-align .frst-labels span {
    right: auto;
    left: -47px;
}

/*--------- news-------*/

.frst-timeline-style-8 .frst-timeline-content-inner {
    border: 2px solid #d8d8d8;
    padding: 30px;
    position: relative;
    -webkit-border-radius: 4px;
    border-radius: 4px;
}

.frst-timeline-style-8 .frst-timeline-content-inner .media-section {
    width: 100%;
    max-height: 350px;
    overflow: hidden;
    margin-bottom: 20px;
}

.frst-timeline-style-8 .frst-timeline-content-inner .media-section iframe {
    max-width: 100%;
}

.frst-timeline-style-8 .frst-timeline-content-inner::before,
.frst-timeline-style-8 .frst-timeline-content-inner::after {
    content: '';
    border-style: solid;
    border-color: transparent;
    position: absolute;
}

.frst-timeline-style-8 .frst-timeline-content-inner::before {
    border-width: 10px 11px 10px 0;
    border-right-color: #d8d8d8;
    left: -11px;
    top: 10px;
}

.frst-timeline-style-8 .frst-timeline-content-inner::after {
    border-width: 8px 9px 8px 0;
    border-right-color: #fff;
    left: -9px;
    top: 12px;
}

.frst-timeline-style-8.frst-right-align .frst-timeline-content-inner::before {
    border-width: 10px 0 10px 11px;
    border-left-color: #d8d8d8;
    left: auto;
    right: -11px;
}

.frst-timeline-style-8.frst-right-align .frst-timeline-content-inner::after {
    border-width: 8px 0 8px 9px;
    border-left-color: #fff;
    right: -8px;
    left: auto;
}

/*---------- Responsive part ------------*/

@media only screen and (min-width: 679px) {
    .frst-date-opposite {
        margin-left: 203px;
    }

    .frst-left-align.frst-date-opposite {
        margin-left: 203px;
    }

    .frst-right-align.frst-date-opposite {
        margin-right: 203px;
        margin-left: 0;
    }

    .frst-timeline-style-8.frst-alternate .frst-timeline-content,
    .frst-timeline-style-8.frst-date-opposite .frst-timeline-content {
        width: calc(50% - 44px);
    }

    .frst-timeline-style-8.frst-date-opposite .frst-date {
        font-family: 'Frutiger Neue LT W06 Book', Arial, sans-serif;
        left: -248px;
        line-height: 32px;
        width: 160px;
    }

    .frst-timeline-style-8.frst-date-opposite.frst-right-align .frst-date,
    .frst-timeline-style-8.frst-date-opposite.frst-alternate .frst-timeline-block.frst-odd-item .frst-date {
        right: -248px;
        left: auto;
        text-align: left;
    }

    .frst-timeline-style-8.frst-right-align .frst-date {
        left: auto;
        right: 0;
    }

    /*--------- news-------*/
    .frst-timeline-style-8.frst-alternate .frst-odd-item .frst-timeline-content-inner::before {
        border-width: 10px 0 10px 11px;
        border-left-color: #d8d8d8;
        left: auto;
        right: -11px;
    }

    .frst-timeline-style-8.frst-alternate .frst-odd-item .frst-timeline-content-inner::after {
        border-width: 8px 0 8px 9px;
        border-left-color: #fff;
        right: -8px;
        left: auto;
    }
}

@media only screen and (max-width: 678px) {
    .frst-timeline-style-8.frst-responsive-right .frst-labels span {
        left: auto;
        right: -47px;
    }

    .frst-timeline-style-8.frst-responsive-right .frst-timeline-block::before {
        margin-right: -2px;
    }

    .frst-timeline-style-8.frst-responsive-right .frst-timeline-img {
        margin-right: -21px;
    }

    /*--------- news-------*/
    .frst-timeline-style-8.frst-responsive-right .frst-timeline-content-inner::before {
        border-width: 10px 0 10px 11px;
        border-left-color: #d8d8d8;
        left: auto;
        right: -11px;
    }

    .frst-timeline-style-8.frst-responsive-right .frst-timeline-content-inner::after {
        border-width: 8px 0 8px 9px;
        border-left-color: #fff;
        right: -8px;
        left: auto;
    }
}

    .frst-alternate .frst-timeline-block.frst-odd-item .frst-timeline-content, 
    .frst-date-opposite.frst-left-align .frst-date, 
    .frst-right-align {
        text-align: left;
    }

    .frst-alternate.frst-date-opposite {
        .frst-timeline-block.frst-odd-item .frst-date {
            line-height: 2em;
            text-align: left;
        }
        
        .frst-timeline-block.frst-even-item .frst-date  {
            line-height: 2em;
            text-align: right;
        }
    }

    .frst-timeline-style-8 .frst-timeline-content-inner {
        padding: 1em 1em 0 1em;

        h2 {
            font-family: $roman-normal-bold;
            margin-top: 0;
        }

        p {margin-bottom: 15px;}
    }

    .frst-timeline-label-block {
        padding-left: 0;

        .frst-labels {
            margin-left: -50px;
            
            span {
                background-color: $dark-blue;
                font-size: 20px;
            }
        }
    }

    .frst-timeline-style-8 .frst-timeline-img span {
        background-color: $dark-gray-new; 
        border:$dark-gray-new solid 3px;   	
        &.green {
            background-color: $teal; 
            border:$teal solid 3px;   	
        } 	
        &.red {
            background-color: $red; 
            border:$red solid 3px;   	
        } 	
        &.yellow {
            background-color: $yellow; 
            border:$yellow solid 3px;   	
        }
        &.dark-blue {
            background-color: $carnegie-blue; 
            border:$carnegie-blue solid 3px;   	
        }
    }

    .frst-timeline-style-8 .frst-timeline-block::before {background: rgba(110, 110, 113, 0.8) none repeat scroll 0 0;}
    .frst-timeline-style-8 .frst-timeline-content-inner {
        &.gray {
            &::before {
                border-width: 10px 11px 10px 0;
                border-right-color: $dark-gray-new;
                left: -11px;
                top: 10px;
            }
            &::after {
                border-width: 8px 9px 8px 0;
                border-right-color: $light-gray-new;
                left: -9px;
                top: 12px;
            }
        }
        &.green {
            &::before {
                border-right-color: $teal;
            }
        }
        &.red {
            &::before {
                border-right-color: $red;
            }
        }
        &.yellow {
            &::before {
                border-right-color: $yellow;
            }
        }
        &.dark-blue {
            &::before {
                border-right-color: $carnegie-blue;
            }
        }
    }

    .frst-date {
        color: $dark-blue;
        font-family: $roman-normal-book;
        font-size: 1.1em;
        line-height: 2em;
        text-transform: uppercase;
    }

    .frst-timeline-style-8 li {
        margin: 0;
        font-family: $roman-normal-book;
        font-size: 1.3rem;
        line-height: normal;
    }

    .frst-timeline-style-8 p {
        color: $tertiary;
        font-size: 1.3rem;
        font-family: $roman-normal-book
    }

    .frst-timeline-style-8 h4 {
        color: $black;
        font-size: 0.9rem;
        margin-top: 2em;
        text-transform: uppercase;
    }

    .css-timeline li {
        margin-top: 1em;
    }

    .frst-timeline-content-inner {
        &.gray {background-color: $light-gray-new; border: $dark-gray-new 1px solid}
        &.green {border: $teal 1px solid}
        &.red {border: $red 1px solid}
        &.yellow {border: $yellow 1px solid}
        &.dark-blue {border: $carnegie-blue 1px solid}
    }

    .legend {
        border-bottom: $dark-gray-new 3px solid;
        margin-bottom: calc($gutter / 2);
        margin-top: $gutter;
        padding-bottom: calc($gutter / 2);
        font-family: $roman-normal-bold;
        .cols {margin-left: 0;}
        .legend-text {
            display: block;
        }
        span {
            background-color: $dark-gray-new; 
            border:$dark-gray-new solid 3px;  
            border-radius: 50%;
            display: inline-block;
            height: $gutter;
            margin-right: $gutter;
            width: $gutter; 	
            &.green {
                background-color: $teal; 
                border:$teal solid 3px;   	
            } 	
            &.red {
                background-color: $red; 
                border:$red solid 3px;   	
            } 	
            &.yellow {
                background-color: $yellow; 
                border:$yellow solid 3px;   	
            }	
            &.dark-blue {
                background-color: $carnegie-blue;
                border: $carnegie-blue solid 3px;   	
            }
        }
    }



    @include media-query(tablet) {

        .zone-title {
            background-size: 160% auto;
        }
    }
    @include media-query(phone) {
        .zone-title {
            background-size: cover;
            .container-title {
                width: 75%;
            }
            h1 {
                font-size: 3rem;
            }
        }

        .col {
            width: 100%;
        }
    }
}
</style>